import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { collection, addDoc, doc, updateDoc, deleteDoc, increment, getFirestore } from 'firebase/firestore';
import dayjs from 'dayjs';
import app from '../config/firebase';
import { sincronizarProductoPublico, precioFinal } from '../services/petshopPublico';

const db = getFirestore(app);

export const COLECCION_VENTAS_PETSHOP = 'ventasPetshop';
export const COLECCION_PRODUCTOS_PETSHOP = 'petshop';

// Paleta propia para diferenciarla de "Ventas de Productos" (azul)
const C = { main: '#00897b', dark: '#00695c', light: '#e0f2f1', border: '#b2dfdb' };

const OPCION_OTRO = { id: 'otro', label: '📝 Otro (Producto Personalizado)', stock: null, precio: null };

const esPersonalizada = (venta) => venta.esProductoPersonalizado || venta.productoId === 'otro';

// Ganancia = total - costo de compra; sin costo conocido (personalizado) = total
export const calcularGananciaPetshop = (venta) => {
  if (venta.precioCompra !== undefined && venta.precioCompra !== null) {
    return (venta.total || 0) - venta.precioCompra * (venta.cantidad || 0);
  }
  return venta.total || 0;
};

const crearItem = () => ({
  id: Date.now() + Math.random(),
  productoId: '',
  productoNombre: '',
  cantidad: 1,
  precioUnitario: 0,
  total: 0,
  esProductoPersonalizado: false,
  nombreProductoPersonalizado: ''
});

const VentasPetshopSection = ({
  ventas,
  productos,
  filtroActivo,
  formatearMoneda,
  onRecargar,
  notificar,
  isMobile
}) => {
  const [openAgregar, setOpenAgregar] = useState(false);
  const [datosVenta, setDatosVenta] = useState({ cliente: '', fecha: dayjs().format('YYYY-MM-DD'), items: [] });
  const [openEditar, setOpenEditar] = useState(false);
  const [ventaEdit, setVentaEdit] = useState(null);
  const [ventaVer, setVentaVer] = useState(null);
  const [ventaEliminar, setVentaEliminar] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const productoPorId = (id) => productos.find((p) => p.id === id);
  const opcionDeProducto = (p) => ({
    id: p.id,
    label: p.titulo,
    stock: p.cantidad || 0,
    precio: precioFinal(p)
  });

  // ─── Agregar ──────────────────────────────────────────────────────────────
  const abrirAgregar = () => {
    setDatosVenta({ cliente: '', fecha: dayjs().format('YYYY-MM-DD'), items: [crearItem()] });
    setOpenAgregar(true);
  };

  const actualizarItem = (itemId, field, value) => {
    setDatosVenta((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id !== itemId) return item;
        const updated = { ...item, [field]: value };
        if (field === 'productoId') {
          if (value === 'otro') {
            updated.esProductoPersonalizado = true;
            updated.productoNombre = '';
            updated.precioUnitario = 0;
          } else {
            updated.esProductoPersonalizado = false;
            updated.nombreProductoPersonalizado = '';
            const prod = productoPorId(value);
            if (prod) {
              updated.productoNombre = prod.titulo;
              updated.precioUnitario = precioFinal(prod);
            } else {
              updated.productoNombre = '';
              updated.precioUnitario = 0;
            }
          }
        }
        if (field === 'nombreProductoPersonalizado') updated.productoNombre = value;
        updated.total = (parseFloat(updated.cantidad) || 0) * (parseFloat(updated.precioUnitario) || 0);
        return updated;
      })
    }));
  };

  const guardarVentas = async () => {
    if (guardando) return;
    try {
      setGuardando(true);
      if (!datosVenta.cliente || !datosVenta.fecha || datosVenta.items.length === 0) {
        notificar('Complete el cliente, la fecha y al menos un producto', 'error');
        return;
      }

      // Stock solicitado por producto (suma si el mismo producto aparece varias veces)
      const solicitado = {};
      for (const item of datosVenta.items) {
        if (!item.productoId || !(parseInt(item.cantidad) > 0)) {
          notificar('Complete todos los campos de cada producto', 'error');
          return;
        }
        if (item.esProductoPersonalizado) {
          if (!item.nombreProductoPersonalizado) {
            notificar('Ingrese el nombre del producto personalizado', 'error');
            return;
          }
        } else {
          if (!productoPorId(item.productoId)) {
            notificar('Producto no encontrado', 'error');
            return;
          }
          solicitado[item.productoId] = (solicitado[item.productoId] || 0) + parseInt(item.cantidad);
        }
      }
      for (const [id, cant] of Object.entries(solicitado)) {
        const prod = productoPorId(id);
        if (cant > (prod.cantidad || 0)) {
          notificar(`Stock insuficiente para "${prod.titulo}". Disponible: ${prod.cantidad || 0}`, 'error');
          return;
        }
      }

      for (const item of datosVenta.items) {
        const personalizado = item.productoId === 'otro';
        const prod = personalizado ? null : productoPorId(item.productoId);
        await addDoc(collection(db, COLECCION_VENTAS_PETSHOP), {
          productoId: item.productoId,
          productoNombre: item.productoNombre,
          cantidad: parseInt(item.cantidad),
          precioUnitario: parseFloat(item.precioUnitario),
          total: parseFloat(item.total),
          precioCompra: prod?.precioCompra || 0,
          cliente: datosVenta.cliente,
          fecha: datosVenta.fecha,
          timestamp: new Date(),
          tipo: 'venta_petshop',
          esProductoPersonalizado: personalizado
        });
        if (prod) {
          await updateDoc(doc(db, COLECCION_PRODUCTOS_PETSHOP, item.productoId), {
            cantidad: increment(-parseInt(item.cantidad)),
            ultimaVenta: new Date()
          });
          await sincronizarProductoPublico(item.productoId);
        }
      }

      await onRecargar();
      setOpenAgregar(false);
      const n = datosVenta.items.length;
      notificar(`${n} producto${n > 1 ? 's' : ''} de petshop registrado${n > 1 ? 's' : ''} exitosamente`);
    } catch (error) {
      console.error('Error guardando venta petshop:', error);
      notificar(error.code === 'permission-denied' ? 'No tiene permisos para realizar esta operación' : 'Error al guardar la venta', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // ─── Editar ───────────────────────────────────────────────────────────────
  const abrirEditar = (venta) => {
    setVentaEdit({
      ...venta,
      cantidad: venta.cantidad,
      precioUnitario: venta.precioUnitario,
      total: venta.total
    });
    setOpenEditar(true);
  };

  const cambiarEdit = (field, value) => {
    setVentaEdit((prev) => {
      const updated = { ...prev, [field]: value };
      updated.total = (parseFloat(updated.cantidad) || 0) * (parseFloat(updated.precioUnitario) || 0);
      return updated;
    });
  };

  const guardarEdicion = async () => {
    if (guardando || !ventaEdit) return;
    try {
      setGuardando(true);
      const cantidadNueva = parseInt(ventaEdit.cantidad);
      if (!(cantidadNueva > 0) || !ventaEdit.cliente || !ventaEdit.productoNombre) {
        notificar('Por favor complete todos los campos obligatorios', 'error');
        return;
      }

      const personalizado = esPersonalizada(ventaEdit);
      const prod = personalizado ? null : productoPorId(ventaEdit.productoId);
      const original = ventas.find((v) => v.id === ventaEdit.id);
      const diferencia = cantidadNueva - (original?.cantidad || 0);

      if (prod && diferencia > (prod.cantidad || 0)) {
        notificar(`Stock insuficiente. Disponible: ${prod.cantidad || 0} unidades`, 'error');
        return;
      }

      await updateDoc(doc(db, COLECCION_VENTAS_PETSHOP, ventaEdit.id), {
        productoNombre: ventaEdit.productoNombre,
        cantidad: cantidadNueva,
        precioUnitario: parseFloat(ventaEdit.precioUnitario) || 0,
        total: parseFloat(ventaEdit.total) || 0,
        cliente: ventaEdit.cliente,
        fecha: ventaEdit.fecha,
        fechaModificacion: new Date()
      });

      if (prod && diferencia !== 0) {
        await updateDoc(doc(db, COLECCION_PRODUCTOS_PETSHOP, ventaEdit.productoId), {
          cantidad: increment(-diferencia),
          ultimaModificacion: new Date()
        });
        await sincronizarProductoPublico(ventaEdit.productoId);
      }

      await onRecargar();
      setOpenEditar(false);
      setVentaEdit(null);
      notificar('Venta de petshop actualizada exitosamente');
    } catch (error) {
      console.error('Error editando venta petshop:', error);
      notificar('Error al actualizar la venta', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // ─── Eliminar ─────────────────────────────────────────────────────────────
  const confirmarEliminar = async () => {
    if (guardando || !ventaEliminar) return;
    try {
      setGuardando(true);
      await deleteDoc(doc(db, COLECCION_VENTAS_PETSHOP, ventaEliminar.id));

      const personalizado = esPersonalizada(ventaEliminar);
      const existe = !personalizado && productoPorId(ventaEliminar.productoId);
      if (existe) {
        await updateDoc(doc(db, COLECCION_PRODUCTOS_PETSHOP, ventaEliminar.productoId), {
          cantidad: increment(ventaEliminar.cantidad || 0),
          ultimaModificacion: new Date()
        });
        await sincronizarProductoPublico(ventaEliminar.productoId);
      }

      await onRecargar();
      notificar(existe ? 'Venta eliminada exitosamente. Stock restaurado' : 'Venta eliminada exitosamente');
      setVentaEliminar(null);
    } catch (error) {
      console.error('Error eliminando venta petshop:', error);
      notificar('Error al eliminar el registro', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const acciones = (venta) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
      <Tooltip title="Ver detalle">
        <IconButton size="small" onClick={() => setVentaVer(venta)} sx={{ color: '#c46494' }}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => abrirEditar(venta)} sx={{ color: C.main }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" onClick={() => setVentaEliminar(venta)} sx={{ color: '#f44336' }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const totalAgregar = datosVenta.items.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <>
      <Paper sx={{ p: isMobile ? 1 : 1.5, minHeight: isMobile ? 'auto' : '300px', height: 'auto', mb: isMobile ? 1.5 : 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            mb: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}
        >
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 'bold', color: C.main }}>
            Ventas de Petshop
          </Typography>
          <Button
            variant="contained"
            size={isMobile ? 'medium' : 'small'}
            startIcon={<AddIcon />}
            onClick={abrirAgregar}
            fullWidth={isMobile}
            sx={{ backgroundColor: C.main, '&:hover': { backgroundColor: C.dark } }}
          >
            Agregar Venta
          </Button>
        </Box>

        {!isMobile ? (
          <TableContainer sx={{ maxHeight: '250px', overflowX: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120 }}><strong>Producto</strong></TableCell>
                  <TableCell sx={{ minWidth: 80, textAlign: 'center' }}><strong>Cant.</strong></TableCell>
                  <TableCell sx={{ minWidth: 100, textAlign: 'right' }}><strong>Total</strong></TableCell>
                  <TableCell sx={{ minWidth: 100, textAlign: 'right' }}><strong>Ganancia</strong></TableCell>
                  <TableCell sx={{ minWidth: 120 }}><strong>Cliente</strong></TableCell>
                  <TableCell sx={{ minWidth: 100, textAlign: 'center' }}><strong>Fecha</strong></TableCell>
                  <TableCell sx={{ minWidth: 140, textAlign: 'center' }}><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ventas.map((venta) => (
                  <TableRow key={venta.id}>
                    <TableCell sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{venta.productoNombre}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{venta.cantidad}</TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: C.main }}>
                      {formatearMoneda(venta.total)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#e65100' }}>
                      {formatearMoneda(calcularGananciaPetshop(venta))}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{venta.cliente}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontSize: '0.875rem' }}>
                      {dayjs(venta.fecha).format('DD/MM/YYYY')}
                    </TableCell>
                    <TableCell>{acciones(venta)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
            {ventas.map((venta) => (
              <Card key={venta.id} sx={{ mb: 1.5, border: '1px solid #e0e0e0', borderLeft: `4px solid ${C.main}` }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: C.main, lineHeight: 1.2, mb: 0.5 }}>
                        {venta.productoNombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Cliente: {venta.cliente}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                        📅 {dayjs(venta.fecha).format('DD/MM/YYYY')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 1 }}>
                      <Typography variant="h6" sx={{ color: C.dark, fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                        {formatearMoneda(venta.total)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', fontSize: '0.75rem', display: 'block' }}>
                        💰 {formatearMoneda(calcularGananciaPetshop(venta))}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        Cant: {venta.cantidad}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1 }}>{acciones(venta)}</Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {ventas.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <StorefrontIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {filtroActivo
                ? 'No se encontraron ventas de petshop en el rango de fechas seleccionado'
                : 'No hay ventas de petshop registradas'}
            </Typography>
            <Typography variant="body2">
              {filtroActivo ? 'Intenta con un rango de fechas diferente' : 'Comienza agregando tu primera venta'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Modal: registrar venta */}
      <Dialog open={openAgregar} onClose={() => setOpenAgregar(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1, backgroundColor: C.light, color: C.dark, fontWeight: 'bold' }}>
          🐾 Registrar Venta de Petshop
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 1.5 : 3 }}>
          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cliente *"
                value={datosVenta.cliente}
                onChange={(e) => setDatosVenta((prev) => ({ ...prev, cliente: e.target.value }))}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha *"
                type="date"
                value={datosVenta.fecha}
                onChange={(e) => setDatosVenta((prev) => ({ ...prev, fecha: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }}>
            <Chip label={`Productos (${datosVenta.items.length})`} sx={{ backgroundColor: C.light, color: C.dark }} />
          </Divider>

          {datosVenta.items.map((item, index) => {
            const prod = productoPorId(item.productoId);
            return (
              <Paper
                key={item.id}
                sx={{ p: isMobile ? 1.5 : 2, mb: 1.5, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.main}`, backgroundColor: '#fafafa' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: C.main }}>
                    Producto #{index + 1}
                  </Typography>
                  {datosVenta.items.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => setDatosVenta((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== item.id) }))}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      options={[OPCION_OTRO, ...productos.map(opcionDeProducto)]}
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={item.productoId === 'otro' ? OPCION_OTRO : prod ? opcionDeProducto(prod) : null}
                      onChange={(e, newValue) => actualizarItem(item.id, 'productoId', newValue?.id || '')}
                      noOptionsText="No se encontraron productos de petshop"
                      renderInput={(params) => <TextField {...params} label="Producto *" />}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.875rem' }}>
                              {option.label}
                              {option.precio !== null ? ` — ${formatearMoneda(option.precio)}` : ''}
                            </Typography>
                            {option.stock !== null && (
                              <Chip
                                label={`Stock: ${option.stock}`}
                                color={option.stock <= 5 ? 'error' : option.stock <= 10 ? 'warning' : 'success'}
                                size="small"
                                sx={{ ml: 1, flexShrink: 0 }}
                              />
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                  {item.esProductoPersonalizado && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Nombre del Producto *"
                        value={item.nombreProductoPersonalizado}
                        onChange={(e) => actualizarItem(item.id, 'nombreProductoPersonalizado', e.target.value)}
                        placeholder="Ingresa el nombre del producto..."
                      />
                    </Grid>
                  )}
                  <Grid item xs={4} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Cantidad"
                      type="number"
                      value={item.cantidad}
                      onChange={(e) => actualizarItem(item.id, 'cantidad', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Precio Unit."
                      type="number"
                      value={item.precioUnitario}
                      onChange={(e) => actualizarItem(item.id, 'precioUnitario', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      InputProps={{ startAdornment: '$' }}
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Subtotal"
                      value={formatearMoneda(item.total)}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: C.light } }}
                    />
                  </Grid>
                  {!item.esProductoPersonalizado && prod && (
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Stock disponible"
                        value={prod.cantidad || 0}
                        InputProps={{ readOnly: true }}
                        helperText="En petshop"
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            onClick={() => setDatosVenta((prev) => ({ ...prev, items: [...prev.items, crearItem()] }))}
            sx={{ color: C.main, mb: 2 }}
            size="small"
          >
            Agregar otro producto
          </Button>

          {datosVenta.items.length > 0 && (
            <Box sx={{ textAlign: 'right', p: 1.5, bgcolor: C.light, borderRadius: 1 }}>
              <Typography variant="h6" sx={{ color: C.dark, fontWeight: 'bold' }}>
                Total: {formatearMoneda(totalAgregar)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 1.5 : 3, pb: isMobile ? 2 : 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setOpenAgregar(false)} fullWidth={isMobile}>Cancelar</Button>
          <Button
            onClick={guardarVentas}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={guardando}
            fullWidth={isMobile}
            sx={{ backgroundColor: C.main, '&:hover': { backgroundColor: C.dark } }}
          >
            {guardando ? 'Guardando...' : `Guardar Venta${datosVenta.items.length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: editar venta */}
      <Dialog open={openEditar} onClose={() => setOpenEditar(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ backgroundColor: C.light, color: C.dark, fontWeight: 'bold' }}>
          Editar Venta de Petshop
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          {ventaEdit && (() => {
            const prod = esPersonalizada(ventaEdit) ? null : productoPorId(ventaEdit.productoId);
            const original = ventas.find((v) => v.id === ventaEdit.id);
            const stockMax = prod ? (prod.cantidad || 0) + (original?.cantidad || 0) : undefined;
            return (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Producto"
                    value={ventaEdit.productoNombre}
                    onChange={(e) => cambiarEdit('productoNombre', e.target.value)}
                    InputProps={{ readOnly: !esPersonalizada(ventaEdit) }}
                    helperText={esPersonalizada(ventaEdit) ? 'Producto personalizado' : 'Para cambiar de producto, elimina la venta y regístrala de nuevo'}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Cantidad"
                    type="number"
                    value={ventaEdit.cantidad}
                    onChange={(e) => cambiarEdit('cantidad', e.target.value)}
                    inputProps={{ min: 1, max: stockMax }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Stock Disponible"
                    value={prod ? stockMax : 'N/A'}
                    InputProps={{ readOnly: true }}
                    helperText="Incluye cantidad actual"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Precio Unitario"
                    type="number"
                    value={ventaEdit.precioUnitario}
                    onChange={(e) => cambiarEdit('precioUnitario', e.target.value)}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Cliente" value={ventaEdit.cliente} onChange={(e) => cambiarEdit('cliente', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha"
                    type="date"
                    value={ventaEdit.fecha}
                    onChange={(e) => cambiarEdit('fecha', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Total" value={formatearMoneda(ventaEdit.total)} InputProps={{ readOnly: true }} />
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditar(false)}>Cancelar</Button>
          <Button
            onClick={guardarEdicion}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={guardando}
            sx={{ backgroundColor: C.main, '&:hover': { backgroundColor: C.dark } }}
          >
            {guardando ? 'Actualizando...' : 'Actualizar Venta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: ver detalle */}
      <Dialog open={!!ventaVer} onClose={() => setVentaVer(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: C.light, color: C.dark, fontWeight: 'bold' }}>
          Detalle de Venta de Petshop
        </DialogTitle>
        <DialogContent>
          {ventaVer && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography><strong>Producto:</strong> {ventaVer.productoNombre}</Typography>
              <Typography><strong>Cantidad:</strong> {ventaVer.cantidad}</Typography>
              <Typography><strong>Precio unitario:</strong> {formatearMoneda(ventaVer.precioUnitario)}</Typography>
              <Typography><strong>Total:</strong> {formatearMoneda(ventaVer.total)}</Typography>
              <Typography><strong>Ganancia:</strong> {formatearMoneda(calcularGananciaPetshop(ventaVer))}</Typography>
              <Typography><strong>Cliente:</strong> {ventaVer.cliente}</Typography>
              <Typography><strong>Fecha:</strong> {dayjs(ventaVer.fecha).format('DD/MM/YYYY')}</Typography>
              {esPersonalizada(ventaVer) && <Chip label="Producto personalizado" size="small" sx={{ alignSelf: 'flex-start' }} />}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVentaVer(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal: confirmar eliminación */}
      <Dialog open={!!ventaEliminar} onClose={() => setVentaEliminar(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar la venta de <strong>{ventaEliminar?.productoNombre}</strong> ({ventaEliminar?.cantidad} und.)?
          </Typography>
          {ventaEliminar && !esPersonalizada(ventaEliminar) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              El stock se devolverá al petshop.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVentaEliminar(null)}>Cancelar</Button>
          <Button onClick={confirmarEliminar} variant="contained" color="error" disabled={guardando}>
            {guardando ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VentasPetshopSection;
